import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Award, TrendingUp, DollarSign, CheckCircle, ArrowRight, Gift, Target, ArrowUpRight } from 'lucide-react';

const ReferralProgramPage = () => {
  const navigate = useNavigate();

  const tiers = [
    { name: 'Bronze', range: '0-9 referrals', commission: '150', color: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
    { name: 'Silver', range: '10-19 referrals', commission: '200', color: '#374151', bg: '#F9FAFB', border: '#D1D5DB', featured: true },
    { name: 'Gold', range: '20+ referrals', commission: '250', color: '#92400E', bg: '#FFFBEB', border: '#FCD34D' },
  ];

  const benefits = [
    { icon: DollarSign, title: 'Earn Per Referral', desc: 'Get paid for every successful registration through your unique code' },
    { icon: TrendingUp, title: 'Tier-Based Growth', desc: 'Higher referrals unlock better commission rates automatically' },
    { icon: Target, title: 'Real-Time Tracking', desc: 'Monitor referrals, conversions, and earnings from your dashboard' },
    { icon: Gift, title: 'No Cap on Earnings', desc: 'Unlimited earning potential with no monthly or yearly ceiling' },
  ];

  const steps = [
    { num: '01', title: 'Sign Up', desc: 'Create your referral partner account in under 2 minutes' },
    { num: '02', title: 'Get Approved', desc: 'Quick admin verification to activate your partnership' },
    { num: '03', title: 'Share Code', desc: 'Distribute your unique referral code across your network' },
    { num: '04', title: 'Earn Money', desc: 'Receive commission when referred teams register & pay' },
  ];

  return (
    <div style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }} className="min-h-screen">
      {/* Hero */}
      <div style={{ background: '#0A0A0A' }} className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, #fff 0px, #fff 1px, transparent 1px, transparent 60px), repeating-linear-gradient(0deg, #fff 0px, #fff 1px, transparent 1px, transparent 60px)'
        }} />
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-4" style={{ color: '#10B981', fontFamily: "'IBM Plex Mono', monospace" }}>
                Referral Partner Program
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Earn <span style={{ color: '#10B981' }}>&#8377;150-&#8377;250</span>
                <br />Per Referral
              </h1>
              <p className="text-base max-w-md" style={{ color: 'rgba(255,255,255,0.5)', lineHeight: '1.7' }}>
                Join the EvalentX Referral Program and earn money by spreading the word about India's premier tech community platform.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/referral/register')}
                className="flex items-center justify-center gap-2 px-7 py-3 text-sm font-bold text-white transition-all"
                style={{ background: '#10B981', borderRadius: '2px' }}
                data-testid="become-partner-btn"
              >
                Become a Partner <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate('/referral/login')}
                className="flex items-center justify-center gap-2 px-7 py-3 text-sm font-semibold transition-all"
                style={{ border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: '2px' }}
                data-testid="partner-login-btn"
              >
                Partner Login
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Commission Tiers */}
      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
          Commission Tiers
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ color: '#0A0A0A' }}>
          Your earnings grow with you
        </h2>
        <p className="text-sm mb-10" style={{ color: '#71717A', maxWidth: '480px' }}>
          Higher referral counts unlock better commission rates. No cap, no limits.
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          {tiers.map((tier) => (
            <div key={tier.name} className="relative p-6 bg-white transition-all group"
              style={{ border: tier.featured ? '2px solid #002FA7' : '1px solid #E4E4E7', borderRadius: '2px' }}
              data-testid={`tier-${tier.name.toLowerCase()}`}
            >
              {tier.featured && (
                <span className="absolute -top-3 left-4 px-2 py-0.5 text-[10px] font-bold tracking-[0.1em] uppercase text-white" style={{ background: '#002FA7', borderRadius: '2px' }}>
                  Popular
                </span>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center" style={{ background: '#F4F4F5', borderRadius: '2px' }}>
                  <Award size={20} style={{ color: '#002FA7' }} />
                </div>
                <div>
                  <h3 className="text-lg font-bold" style={{ color: '#0A0A0A' }}>{tier.name}</h3>
                  <p className="text-xs" style={{ color: '#71717A' }}>{tier.range}</p>
                </div>
              </div>
              <div className="pt-4" style={{ borderTop: '1px solid #E4E4E7' }}>
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: '#A1A1AA' }}>Per referral</p>
                <p className="text-3xl font-black tracking-tight" style={{ color: '#0A0A0A', fontFamily: "'IBM Plex Mono', monospace" }}>
                  &#8377;{tier.commission}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What Counts */}
      <div style={{ background: '#fff', borderTop: '1px solid #E4E4E7', borderBottom: '1px solid #E4E4E7' }}>
        <div className="max-w-5xl mx-auto px-6 py-14 lg:py-16">
          <div className="flex items-start gap-4 mb-6">
            <CheckCircle size={24} style={{ color: '#059669', marginTop: '2px' }} />
            <div>
              <h2 className="text-xl font-bold tracking-tight" style={{ color: '#0A0A0A' }}>What Counts as a Successful Referral?</h2>
              <p className="text-sm mt-1" style={{ color: '#71717A' }}>Three conditions must be met for commission credit</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 ml-0 sm:ml-10">
            {[
              'A new team registers using your unique referral code',
              'The team completes their payment successfully',
              'Your commission is credited instantly to your dashboard',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3 p-4" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '2px' }}>
                <span className="text-sm font-bold flex-shrink-0" style={{ color: '#059669', fontFamily: "'IBM Plex Mono', monospace" }}>{String(i + 1).padStart(2, '0')}</span>
                <p className="text-sm" style={{ color: '#065F46' }}>{text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs ml-0 sm:ml-10 p-3" style={{ color: '#71717A', background: '#F4F4F5', borderRadius: '2px' }}>
            <strong style={{ color: '#0A0A0A' }}>Note:</strong> Only paid registrations count. Pending or cancelled registrations will not earn commission.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
        <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
          How It Works
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-10" style={{ color: '#0A0A0A' }}>
          Four steps to start earning
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step) => (
            <div key={step.num} className="p-5 bg-white transition-all hover:-translate-y-1"
              style={{ border: '1px solid #E4E4E7', borderRadius: '2px' }}
              data-testid={`step-${step.num}`}
            >
              <span className="text-3xl font-black tracking-tight block mb-3" style={{ color: '#E4E4E7', fontFamily: "'IBM Plex Mono', monospace" }}>
                {step.num}
              </span>
              <h3 className="text-base font-bold mb-1" style={{ color: '#0A0A0A' }}>{step.title}</h3>
              <p className="text-sm" style={{ color: '#71717A', lineHeight: '1.6' }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div style={{ background: '#fff', borderTop: '1px solid #E4E4E7', borderBottom: '1px solid #E4E4E7' }}>
        <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
          <p className="text-[10px] font-bold tracking-[0.25em] uppercase mb-3" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
            Benefits
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-10" style={{ color: '#0A0A0A' }}>
            Why join our program?
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <div key={i} className="p-5 transition-all group" style={{ background: '#FAFAFA', border: '1px solid #E4E4E7', borderRadius: '2px' }}>
                <div className="w-10 h-10 flex items-center justify-center mb-4" style={{ background: '#fff', border: '1px solid #E4E4E7', borderRadius: '2px' }}>
                  <b.icon size={18} style={{ color: '#002FA7' }} />
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: '#0A0A0A' }}>{b.title}</h3>
                <p className="text-sm" style={{ color: '#71717A', lineHeight: '1.5' }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
        <div className="p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6"
          style={{ background: '#0A0A0A', borderRadius: '2px' }}>
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-2">Ready to Start Earning?</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Join hundreds of partners already earning with EvalentX
            </p>
          </div>
          <button
            onClick={() => navigate('/referral/register')}
            className="flex items-center justify-center gap-2 px-8 py-3 text-sm font-bold text-white transition-all flex-shrink-0"
            style={{ background: '#10B981', borderRadius: '2px' }}
            data-testid="cta-become-partner"
          >
            Become a Referral Partner <ArrowUpRight size={16} />
          </button>
        </div>
      </div>

      {/* Back link */}
      <div className="pb-10 px-6 text-center">
        <Link to="/" className="text-sm font-medium transition-colors hover:text-[#002FA7]" style={{ color: '#71717A' }} data-testid="back-to-home">
          &larr; Back to EvalentX
        </Link>
      </div>
    </div>
  );
};

export default ReferralProgramPage;
